<template>
  <div class="modal fade" id="moveLocationModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered w-[700px]">
      <div class="modal-content">
        <div class="modal-header">
          <h1 class="modal-title fs-5">Move Location</h1>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body" v-if="props.license">
          <div class="text-sm space-y-2 overflow-hidden">
            <div v-if="props.license.m_type !== 'PROXYSMART'" class="text-red-600 text-sm font-semibold">
              This feature is only available for ProxySmart proxies.
            </div>

            <div class="pt-2">
              <div class="float-left w-1/2 px-5 h-20 modal-item">
                Modem Name:
                <v-combobox
                  v-model="selectedModemName"
                  :items="modemList.map(m => m.name)"
                  variant="outlined"
                  density="compact"
                  :hide-no-data="false"
                  clearable
                  :disabled="loadingList || props.license.m_type !== 'PROXYSMART'"
                />
                <div v-if="loadingList" class="text-xs text-gray-500 mt-1">Loading available servers…</div>
              </div>

              <div class="float-left w-1/2 px-5 h-20 modal-item">
                ISP:
                <v-text-field v-model="selectedModemISP" variant="outlined" density="compact" readonly />
              </div>

              <div class="float-left w-1/2 px-5 h-20 modal-item">
                Proxies:
                <v-select
                  v-model="selectedProxy"
                  :items="proxyList.map(p => `${p.shared_port}/${p.brother_port}`)"
                  variant="outlined"
                  density="compact"
                  :disabled="loadingDetails || props.license.m_type !== 'PROXYSMART'"
                />
                <div v-if="loadingDetails" class="text-xs text-gray-500 mt-1">Loading proxies/packages…</div>
              </div>

              <div class="float-left w-1/2 px-5 h-20 modal-item">
                Packages:
                <v-select
                  v-model="selectedPackage"
                  :items="packageList.map(pkg => pkg.name)"
                  variant="outlined"
                  density="compact"
                  :disabled="loadingDetails || props.license.m_type !== 'PROXYSMART'"
                />
              </div>

              <div class="clear-both"></div>

              <div
                v-if="props.license.m_type === 'PROXYSMART' && modemList.length === 0 && !loadingList"
                class="text-xs text-gray-500 px-5"
              >
                No eligible servers found (must be US + same 4G/5G tier + have available proxies).
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="buttonDisabled"
            @click="move"
          >
            Move
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, watch, computed, defineEmits } from "vue";
import { useProxyStore } from "@/stores";

const props = defineProps({
  license: {
    type: Object,
    required: true,
  },
});

const proxyStore = useProxyStore();
const emit = defineEmits(["close"]);

const loadingList = ref(false);
const loadingDetails = ref(false);
const moving = ref(false);
const modemList = ref([]);
const proxyList = ref([]);
const packageList = ref([]);

const selectedModemName = ref("");
const selectedModemISP = ref("");
const selectedProxy = ref("");
const selectedPackage = ref("");

const buttonDisabled = computed(() => {
  if (!props.license) return true;
  if (props.license.m_type !== "PROXYSMART") return true;
  if (moving.value) return true;
  return !selectedModemName.value || !selectedProxy.value || !selectedPackage.value;
});

const loadModems = async () => {
  if (!props.license || props.license.m_type !== "PROXYSMART") {
    modemList.value = [];
    proxyList.value = [];
    packageList.value = [];
    selectedModemName.value = "";
    selectedModemISP.value = "";
    selectedProxy.value = "";
    selectedPackage.value = "";
    return;
  }
  loadingList.value = true;
  modemList.value = await proxyStore.getMoveLocationModems(props.license.uuid);
  loadingList.value = false;

  if (modemList.value && modemList.value.length > 0) {
    selectedModemName.value = modemList.value[0].name;
  } else {
    selectedModemName.value = "";
  }
};

const loadDetails = async () => {
  if (!props.license || props.license.m_type !== "PROXYSMART") return;
  if (!selectedModemName.value) return;

  const selectedModem = modemList.value.find((m) => m.name === selectedModemName.value);
  selectedModemISP.value = selectedModem ? selectedModem.isp : "";

  loadingDetails.value = true;
  const { proList, pkgList } = await proxyStore.getMoveLocationProxiesAndPackages(
    props.license.uuid,
    selectedModemName.value
  );
  proxyList.value = proList;
  packageList.value = pkgList;
  selectedProxy.value = proList?.length ? `${proList[0].shared_port}/${proList[0].brother_port}` : "";
  selectedPackage.value = pkgList?.length ? pkgList[0].name : "";
  loadingDetails.value = false;
};

const move = async () => {
  if (!props.license || props.license.m_type !== "PROXYSMART") return;
  if (!selectedModemName.value || !selectedProxy.value || !selectedPackage.value) return;
  moving.value = true;
  const res = await proxyStore.moveProxyLocation(
    props.license.uuid,
    selectedModemName.value,
    selectedProxy.value,
    selectedPackage.value
  );
  moving.value = false;
  if (res) {
    await proxyStore.getProxyList();
    emit("close");
  }
};

// When the selected license changes (user clicked action), load eligible servers
watch(
  () => props?.license?.uuid,
  (uuid) => {
    if (!uuid) return;
    modemList.value = [];
    proxyList.value = [];
    packageList.value = [];
    selectedModemName.value = "";
    selectedModemISP.value = "";
    selectedProxy.value = "";
    selectedPackage.value = "";
    if (props.license?.m_type === "PROXYSMART") {
      loadModems();
    }
  },
  { immediate: true }
);

watch(
  () => selectedModemName.value,
  () => {
    proxyList.value = [];
    packageList.value = [];
    selectedProxy.value = "";
    selectedPackage.value = "";
    if (selectedModemName.value) {
      loadDetails();
    } else {
      selectedModemISP.value = "";
    }
  }
);
</script>

<style scoped>
.clear-both {
  clear: both;
}
</style>

